import { ChartSettingsT, VizType } from 'business/definitions';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { WidgetT } from 'routes/Dashboard/components/WidgetsLayout/helpers';
import { RQAPICallWithData } from 'store/APIClient';
import { useDispatch } from 'store/hooks';

type ChartDataPayload = {
    metrics: QueryMetricT[];
    dimensions: QueryDimensionT[];
    filters: QueryFilterT[];
    vizType: VizType;
    title: string;
    settings: ChartSettingsT;
    hasDefinedPosition?: boolean;
    previousWidgetPosition?: FromPositionQueryT;
};

export const useSaveChartFromData = (
    onSuccessAction?: (
        widget: WidgetT,
        redirect: boolean,
        dashboardSlug: string,
    ) => void,
) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const getSnackAction = (
        snackAction: boolean,
        key: number | undefined,
        slug: string,
    ) =>
        snackAction
            ? {
                  message: t('components:common.see'),
                  onClick: () => {
                      if (key) {
                          navigate(`/dashboard/${slug}#grid-chart-${key}`);
                      } else {
                          navigate(`/dashboard/${slug}`);
                      }
                  },
              }
            : undefined;
    const mutation = useMutation({
        mutationFn: (payload: {
            dashboard: { key: number; slug: string };
            chartData: ChartDataPayload;
            redirect: boolean;
            snackAction: boolean;
            fromPosition?: FromPositionQueryT;
        }) => {
            const { chartData, fromPosition } = payload;
            if (fromPosition) {
                chartData.hasDefinedPosition = true;
                chartData.previousWidgetPosition = fromPosition;
            } else {
                chartData.hasDefinedPosition = false;
            }
            return RQAPICallWithData<WidgetT>({
                endpoint: `explore/dashboard/${payload.dashboard.key}/charts`,
                method: 'POST',
                body: { ...chartData },
            });
        },

        onSuccess: (response, { dashboard, redirect, snackAction }) => {
            dispatch.vision.notifyUser({
                message: response.content.message,
                snackAction: getSnackAction(
                    snackAction,
                    response.content.data?.key,
                    dashboard.slug,
                ),
            });
            if (onSuccessAction)
                onSuccessAction(
                    response.content.data,
                    redirect,
                    dashboard.slug,
                );
        },
    });
    return { saveChartFromData: mutation.mutate };
};
